import { AutoComplete, Input } from "antd";
import { useEffect, useRef, useState } from "react";

import { queryAddressSuggestions, sliceGbCode } from "@/views/pages/logistics/wharf";
import type { AreaNode } from "@/utils/areaLib";

export interface AddressSuggestion {
    value: string;
    link?: {
        address?: string;
        name?: string;
        lonlat?: string;
        countyCode?: string;
        gbCode?: string;
    };
}

export interface AreaCascaderOption {
    value: string;
    label: string;
    children?: AreaCascaderOption[];
}

export interface AddressAutocompleteProps {
    "aria-label": string;
    id?: string;
    value?: string;
    regionCode?: string;
    placeholder?: string;
    maxLength?: number;
    disabled?: boolean;
    className?: string;
    onChange?: (value: string) => void;
    onSelectSuggestion?: (suggestion: AddressSuggestion) => void;
}

/** 将公共地区库转换为 Ant Cascader 选项，供所有地址表单保持一致的三级区域结构。 */
export function mapAreaCascaderOptions(items: AreaNode[]): AreaCascaderOption[] {
    return items.map(item => ({
        value: String(item.id),
        label: item.name,
        children: item.childs?.length ? mapAreaCascaderOptions(item.childs) : undefined
    }));
}

/** 从天地图候选的国标码恢复省、市、区编码。 */
export function getSuggestionAreaCodes(suggestion: AddressSuggestion): string[] {
    const areaCode = suggestion.link?.countyCode || suggestion.link?.gbCode;
    return areaCode ? sliceGbCode(areaCode) : [];
}

/** 从天地图候选解析业务接口使用的经纬度结构。 */
export function getSuggestionGeo(suggestion: AddressSuggestion): { lng: number; lat: number } | undefined {
    const [lng, lat] = (suggestion.link?.lonlat || "").split(",").map(Number);
    return Number.isFinite(lng) && Number.isFinite(lat) ? { lng, lat } : undefined;
}

/**
 * 省市区级联配套的详细地址联想控件。
 * 每个实例使用独立请求序号，避免多个表单或快速输入时旧响应覆盖最新候选。
 */
export function AddressAutocomplete({
    "aria-label": ariaLabel,
    id,
    value,
    regionCode = "",
    placeholder = "请输入详细地址",
    maxLength = 200,
    disabled,
    className,
    onChange,
    onSelectSuggestion
}: AddressAutocompleteProps) {
    const [options, setOptions] = useState<Array<{ value: string; label: string; suggestion: AddressSuggestion }>>([]);
    const searchSequence = useRef(0);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(
        () => () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        },
        []
    );

    /** 输入关键字后按当前区县编码查询，且只接收最后一次请求的结果。 */
    const search = (keyword: string) => {
        const sequence = ++searchSequence.current;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        if (!keyword.trim()) {
            setOptions([]);
            return;
        }
        searchTimer.current = setTimeout(async () => {
            const result = (await queryAddressSuggestions(keyword.trim(), regionCode)) as
                | AddressSuggestion[]
                | undefined;
            if (sequence !== searchSequence.current) return;
            setOptions(
                (result || []).map(suggestion => ({
                    value: suggestion.value,
                    label:
                        !regionCode && suggestion.link?.address && suggestion.link.address !== suggestion.value
                            ? `${suggestion.value} ${suggestion.link.address}`
                            : suggestion.value,
                    suggestion
                }))
            );
        }, 100);
    };

    return (
        <AutoComplete
            id={id}
            className={className}
            disabled={disabled}
            options={options}
            value={value}
            onChange={onChange}
            onSearch={search}
            onSelect={(_, option) => {
                onChange?.(option.value);
                onSelectSuggestion?.(option.suggestion);
                setOptions([]);
            }}
        >
            <Input aria-label={ariaLabel} maxLength={maxLength} placeholder={placeholder} />
        </AutoComplete>
    );
}
